import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSaveCompany } from '../api/companies.js';
import Modal from './Modal.jsx';
import ErrorBanner from './ErrorBanner.jsx';

export default function CompanyFormModal({ company = null, onClose, onSaved }) {
    const { t } = useTranslation();
    const isEdit = company != null;
    const [form, setForm] = useState({
        ruc: company?.id ?? '',
        name: company?.name ?? '',
        menuSolUsername: company?.menuSolAccess?.username ?? '',
        menuSolPassword: '',
        sireClientId: company?.sireAccess?.clientId ?? '',
        sireClientSecret: '',
        gDriveFolderName: '',
    });
    const [showSunat, setShowSunat] = useState(false);
    const [showSire, setShowSire] = useState(false);
    const [showGDrive, setShowGDrive] = useState(false);

    const { mutate, isPending, error } = useSaveCompany((data) => {
        onSaved?.(data);
        onClose();
    });

    function update(field) {
        return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.ruc.trim()) return;

        const input = { id: isEdit ? company.id : form.ruc.trim(), name: form.name.trim() };
        if (showSunat && (form.menuSolUsername || form.menuSolPassword)) {
            input.menuSolAccess = { username: form.menuSolUsername, password: form.menuSolPassword };
        }
        if (showSire && (form.sireClientId || form.sireClientSecret)) {
            input.sireAccess = { clientId: form.sireClientId, clientSecret: form.sireClientSecret };
        }
        if (!isEdit && showGDrive && form.gDriveFolderName) {
            input.gDriveAccess = { folderName: form.gDriveFolderName };
        }
        mutate(input);
    }

    return (
        <Modal title={t(isEdit ? 'companies.editModalTitle' : 'companies.modalTitle')} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <ErrorBanner error={error} fallback={t(isEdit ? 'companies.updateError' : 'companies.createError')} />

                <div className="form-row">
                    <label htmlFor="company-ruc">{t('companies.fields.ruc')}</label>
                    <input
                        id="company-ruc"
                        value={form.ruc}
                        onChange={update('ruc')}
                        maxLength={20}
                        required
                        autoFocus={!isEdit}
                        disabled={isEdit}
                    />
                </div>

                <div className="form-row">
                    <label htmlFor="company-name">{t('companies.fields.name')}</label>
                    <input id="company-name" value={form.name} onChange={update('name')} required autoFocus={isEdit} />
                </div>

                <button type="button" className="form-section-toggle" onClick={() => setShowSunat((v) => !v)}>
                    {showSunat ? '− ' : '+ '}
                    {t('companies.sections.sunat')}
                </button>
                {showSunat && (
                    <div className="form-grid">
                        <div className="form-row">
                            <label htmlFor="sol-username">{t('companies.fields.solUsername')}</label>
                            <input id="sol-username" value={form.menuSolUsername} onChange={update('menuSolUsername')} />
                        </div>
                        <div className="form-row">
                            <label htmlFor="sol-password">{t('companies.fields.solPassword')}</label>
                            <input id="sol-password" type="password" value={form.menuSolPassword} onChange={update('menuSolPassword')} />
                        </div>
                    </div>
                )}

                <button type="button" className="form-section-toggle" onClick={() => setShowSire((v) => !v)}>
                    {showSire ? '− ' : '+ '}
                    {t('companies.sections.sire')}
                </button>
                {showSire && (
                    <div className="form-grid">
                        <div className="form-row">
                            <label htmlFor="sire-client-id">{t('companies.fields.sireClientId')}</label>
                            <input id="sire-client-id" value={form.sireClientId} onChange={update('sireClientId')} />
                        </div>
                        <div className="form-row">
                            <label htmlFor="sire-client-secret">{t('companies.fields.sireClientSecret')}</label>
                            <input id="sire-client-secret" type="password" value={form.sireClientSecret} onChange={update('sireClientSecret')} />
                        </div>
                    </div>
                )}

                {!isEdit && (
                    <>
                        <button type="button" className="form-section-toggle" onClick={() => setShowGDrive((v) => !v)}>
                            {showGDrive ? '− ' : '+ '}
                            {t('companies.sections.gdrive')}
                        </button>
                        {showGDrive && (
                            <div className="form-row">
                                <label htmlFor="gdrive-folder">{t('companies.fields.gdriveFolder')}</label>
                                <input id="gdrive-folder" value={form.gDriveFolderName} onChange={update('gDriveFolderName')} />
                            </div>
                        )}
                    </>
                )}

                <div className="modal-actions">
                    <button type="button" className="btn" onClick={onClose}>
                        {t('common.cancel')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isPending || !form.name.trim() || !form.ruc.trim()}>
                        {isEdit
                            ? isPending ? t('companies.updating') : t('companies.update')
                            : isPending ? t('companies.creating') : t('companies.create')}
                    </button>
                </div>
            </form>
        </Modal>
    );
}